// Player weapon controller — ported from zombies/src/systems/weapons.ts. Inventory, ammo/reload,
// fire-rate/automatic, shotgun pellets, Pack-a-Punch, Mystery Box, melee + deploy branches. The ONLY
// coupling to "what a shot hits" is the EnemyField interface below — longfight implements it against
// the RED Army + Tanks (see systems/playercombat.ts) instead of zombies' ZombieManager.

import * as THREE from "three";
import { MELEE, MYSTERY_POOL, MYSTERY_POOLS, PACK_A_PUNCH, WEAPONS, type WeaponDef } from "../data/arsenal";
import type { Input } from "../engine/input";
import type { Player } from "../entities/player";
import { sfx } from "../engine/sfx";

/** What a shot queries — implemented by longfight against the red army + tanks. */
export interface EnemyField {
  hitscan(origin: THREE.Vector3, dir: THREE.Vector3, damage: number, range: number): {
    hit: boolean; killed: boolean; points: number; wallDist: number | null;
  };
  meleeHit(origin: THREE.Vector3, dir: THREE.Vector3, range: number, arc: number, damage: number, hitOut?: THREE.Object3D[]): {
    points: number; kills: number; hits: number;
  };
}

interface WeaponState {
  def: WeaponDef;
  name: string;
  baseDamage: number;
  baseMag: number;
  damage: number;
  magSize: number;
  ammoInMag: number;
  reserve: number;
  reloading: boolean;
  reloadEndsAt: number;
  lastShotAt: number;
  papLevel: number; // 0..10
}

export const PAP_MAX = 10;

export interface ShotResult {
  fired: boolean;
  pointsEarned: number;
  killed: boolean;
  kills: number;
  melee?: boolean;
  meleeImpact?: THREE.Vector3;
  meleeHits?: THREE.Object3D[];
  meleeDir?: THREE.Vector3;
  deploy?: boolean;
  deployOrigin?: THREE.Vector3;
  deployDir?: THREE.Vector3;
  deployDamage?: number;
  deployCount?: number;
}

/** Hitscan weapons with an inventory, switching, pellets, PaP, and Mystery Box. */
export class WeaponSystem {
  private weapons = new Map<string, WeaponState>();
  private order: string[] = [];
  private currentId: string;
  private origin = new THREE.Vector3();
  private dir = new THREE.Vector3();
  reloadMul = 1; // Speed Cola (stacks down)
  fireRateMul = 1; // Double Tap (stacks down -> faster)
  perkDamageMul = 1; // Deadshot (stacks up)
  globalDamageMul = 1; // (reserved) Insta-Kill
  reserveMul = 1;
  changed = false;

  constructor(startId: string) {
    this.currentId = startId;
    this.add(startId, true);
  }

  private makeState(def: WeaponDef): WeaponState {
    return {
      def,
      name: def.name,
      baseDamage: def.damage,
      baseMag: def.magSize,
      damage: def.damage,
      magSize: def.magSize,
      ammoInMag: def.magSize,
      reserve: def.infiniteReserve ? Infinity : def.reserveAmmo,
      reloading: false,
      reloadEndsAt: 0,
      lastShotAt: -Infinity,
      papLevel: 0,
    };
  }

  add(id: string, switchTo = true): boolean {
    const def = WEAPONS[id];
    if (!def) return false;
    const existing = this.weapons.get(id);
    if (existing) {
      existing.reserve = this.maxReserveOf(existing);
      existing.ammoInMag = existing.magSize;
    } else {
      this.weapons.set(id, this.makeState(def));
      this.order.push(id);
    }
    if (switchTo) this.switchTo(id);
    return true;
  }

  private maxReserveOf(s: WeaponState): number {
    if (s.def.infiniteReserve) return Infinity;
    return Math.ceil(s.def.reserveAmmo * (1 + s.papLevel * 0.25) * this.reserveMul);
  }

  /** Mystery Box: pick a random weapon id from a named pool (grant is the caller's job). */
  pickMystery(poolName: string): { id: string; name: string; options: string[]; index: number } {
    const pool = MYSTERY_POOLS[poolName] ?? MYSTERY_POOL;
    const index = (Math.random() * pool.length) | 0;
    const id = pool[index];
    return { id, name: WEAPONS[id].name, options: pool.map((i) => WEAPONS[i].name), index };
  }

  /** Pack-a-Punch the current weapon (up to PAP_MAX). Returns the new name, or null if maxed. */
  packAPunch(): string | null {
    const s = this.current;
    if (s.papLevel >= PAP_MAX) return null;
    s.papLevel++;
    s.damage = Math.round(s.baseDamage * Math.pow(PACK_A_PUNCH.damageMul, s.papLevel));
    s.magSize = Math.ceil(s.baseMag * (1 + s.papLevel * 0.25));
    s.reserve = this.maxReserveOf(s);
    s.ammoInMag = s.magSize;
    s.name = `${s.def.name} +${s.papLevel}`;
    this.changed = true;
    sfx.play("upgrade");
    return s.name;
  }

  get papLevel(): number {
    return this.current.papLevel;
  }
  get equipmentPap(): number {
    let m = 0;
    for (const s of this.weapons.values()) if (s.papLevel > m) m = s.papLevel;
    return m;
  }

  owns(id: string): boolean {
    return this.weapons.has(id);
  }

  /** Max Ammo: top up every owned weapon's mag + reserve. */
  refillAll(): void {
    for (const s of this.weapons.values()) {
      s.reserve = this.maxReserveOf(s);
      s.ammoInMag = s.magSize;
      s.reloading = false;
    }
  }

  switchTo(id: string): void {
    if (!this.weapons.has(id) || id === this.currentId) return;
    this.currentId = id;
    this.changed = true;
    sfx.play("swap");
  }

  get current(): WeaponState {
    return this.weapons.get(this.currentId)!;
  }
  get currentDef(): WeaponDef {
    return this.current.def;
  }

  get loadout(): Array<{ label: string; current: boolean }> {
    return this.order.map((id) => ({ label: this.weapons.get(id)!.name, current: id === this.currentId }));
  }

  get hud() {
    const s = this.current;
    return {
      id: s.def.id,
      name: s.name,
      mag: s.ammoInMag,
      reserve: s.reserve,
      reloading: s.reloading,
      upgraded: s.papLevel > 0,
      papLevel: s.papLevel,
      melee: !!s.def.melee,
      deploy: !!s.def.deploy,
      owned: this.order.slice(),
    };
  }

  update(player: Player, input: Input, enemies: EnemyField): ShotResult {
    const s = this.current;
    const now = performance.now() / 1000;

    for (let i = 0; i < this.order.length && i < 9; i++) {
      if (input.isDown(`Digit${i + 1}`)) this.switchTo(this.order[i]);
    }

    if (s.reloading && now >= s.reloadEndsAt) {
      const need = s.magSize - s.ammoInMag;
      const take = Math.min(need, s.reserve);
      s.ammoInMag += take;
      s.reserve -= take;
      s.reloading = false;
    }

    if (input.wantsReload()) this.tryReload(now);

    // Melee weapon (Briefcase): a cone swing on left-click, no ammo.
    if (s.def.melee) {
      if (!input.consumeFirePressed()) return NO_SHOT;
      const minInterval = (60 / s.def.fireRate) * this.fireRateMul;
      if (now - s.lastShotAt < minInterval) return NO_SHOT;
      s.lastShotAt = now;
      sfx.play("melee", player.position);
      this.origin.copy(player.position);
      player.lookDirection(this.dir);
      this.dir.y = 0;
      if (this.dir.lengthSq() > 0) this.dir.normalize();
      const akimbo = s.papLevel >= 5 ? 2 : 1;
      const dmg = s.damage * this.perkDamageMul * this.globalDamageMul * akimbo;
      const hits: THREE.Object3D[] = [];
      const m = enemies.meleeHit(this.origin, this.dir, s.def.range, MELEE.arc, dmg, hits);
      if (m.hits > 0) sfx.play(m.kills > 0 ? "kill" : "hit");
      const impact = this.origin.clone().addScaledVector(this.dir, s.def.range * 0.6);
      return {
        fired: true, pointsEarned: m.points, killed: m.kills > 0, kills: m.kills,
        melee: true, meleeImpact: impact, meleeHits: hits, meleeDir: this.dir.clone(),
      };
    }

    // Deploy weapon (Chicken Spheres): left-click lobs an exploding entity. Consumes 1 ammo.
    if (s.def.deploy) {
      if (!input.consumeFirePressed()) return NO_SHOT;
      const minInterval = (60 / s.def.fireRate) * this.fireRateMul;
      if (now - s.lastShotAt < minInterval) return NO_SHOT;
      if (s.ammoInMag <= 0) {
        sfx.play("deny");
        this.tryReload(now);
        return NO_SHOT;
      }
      s.ammoInMag--;
      s.lastShotAt = now;
      sfx.play("throw", player.position);
      const origin = player.position.clone();
      player.lookDirection(this.dir);
      this.dir.y = 0;
      if (this.dir.lengthSq() > 0) this.dir.normalize();
      origin.addScaledVector(this.dir, 40);
      const dmg = s.damage * this.perkDamageMul * this.globalDamageMul;
      return {
        fired: true, pointsEarned: 0, killed: false, kills: 0,
        deploy: true, deployOrigin: origin, deployDir: this.dir.clone(),
        deployDamage: dmg, deployCount: s.papLevel >= 5 ? 2 : 1,
      };
    }

    const wantsFire = s.def.automatic ? input.mouseDown : input.consumeFirePressed();
    if (!wantsFire) return NO_SHOT;

    const minInterval = (60 / s.def.fireRate) * this.fireRateMul;
    if (now - s.lastShotAt < minInterval || s.reloading) return NO_SHOT;
    if (s.ammoInMag <= 0) {
      sfx.play("deny");
      this.tryReload(now);
      return NO_SHOT;
    }

    s.ammoInMag--;
    s.lastShotAt = now;
    sfx.play("gun");
    this.origin.copy(player.position);
    player.lookDirection(this.dir);

    let points = 0;
    let killed = false;
    let kills = 0;
    let anyHit = false;
    const pellets = s.def.pellets ?? 1;
    const inacc = s.def.spread ?? 0.03;
    const volleys = s.papLevel >= 5 ? 2 : 1; // PaP 5+ dual wield
    for (let v = 0; v < volleys; v++) {
      for (let p = 0; p < pellets; p++) {
        const d = this.spread(this.dir, inacc);
        const res = enemies.hitscan(this.origin, d, s.damage * this.perkDamageMul * this.globalDamageMul, s.def.range);
        if (res.hit) anyHit = true;
        points += res.points;
        if (res.killed) {
          killed = true;
          kills++;
        }
      }
    }
    if (anyHit) sfx.play(killed ? "kill" : "hit");
    return { fired: true, pointsEarned: points, killed, kills };
  }

  get ownsAll(): boolean {
    return Object.keys(WEAPONS).every((id) => this.weapons.has(id));
  }

  private spread(base: THREE.Vector3, spread: number): THREE.Vector3 {
    const out = base.clone();
    out.x += (Math.random() - 0.5) * 2 * spread;
    out.y += (Math.random() - 0.5) * 2 * spread;
    out.z += (Math.random() - 0.5) * 2 * spread;
    return out.normalize();
  }

  private tryReload(now: number): void {
    const s = this.current;
    if (s.reloading || s.ammoInMag >= s.magSize || s.reserve <= 0) return;
    s.reloading = true;
    s.reloadEndsAt = now + s.def.reloadTime * this.reloadMul;
    sfx.play("reload");
  }

  reset(startId: string): void {
    this.weapons.clear();
    this.order = [];
    this.reloadMul = 1;
    this.fireRateMul = 1;
    this.perkDamageMul = 1;
    this.reserveMul = 1;
    this.currentId = startId;
    this.add(startId, true);
    this.changed = true;
  }
}

const NO_SHOT: ShotResult = { fired: false, pointsEarned: 0, killed: false, kills: 0 };
